import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  Edit as EditIcon,
  EvStation as EvIcon,
  History as HistoryIcon,
  InfoOutlined as InfoIcon,
  LocalGasStation as FuelIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';

const OWNER_TYPE_LABEL = { company: 'รถบริษัท', personal: 'รถส่วนตัว', other: 'อื่น ๆ' };
const OWNER_TYPE_COLOR = { company: 'primary', personal: 'secondary', other: 'default' };
const OWNER_TYPE_ICON = {
  company: <BusinessIcon sx={{ fontSize: 14 }} />,
  personal: <PersonIcon sx={{ fontSize: 14 }} />,
  other: <CarIcon sx={{ fontSize: 14 }} />,
};
const FUEL_TYPES = ['เบนซิน', 'ดีเซล', 'ไฮบริด', 'EV'];
const OWNER_TYPES = ['company', 'personal', 'other'];
const FUEL_COLOR = { เบนซิน: 'warning', ดีเซล: 'default', ไฮบริด: 'success', EV: 'info' };
const FUEL_ICON = {
  เบนซิน: <FuelIcon sx={{ fontSize: 14 }} />,
  ดีเซล: <FuelIcon sx={{ fontSize: 14 }} />,
  ไฮบริด: <FuelIcon sx={{ fontSize: 14 }} />,
  EV: <EvIcon sx={{ fontSize: 14 }} />,
};

const EMPTY_FORM = {
  license_plate: '',
  brand: '',
  model_name: '',
  fuel_type: 'เบนซิน',
  rate_per_km: 4,
  owner_type: 'personal',
  owner_name: '',
  owner_reason: '',
  is_default: false,
};

function StatCard({ label, value, color, icon }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette[color]?.main ?? theme.palette.primary.main, 0.2)}`,
        bgcolor: alpha(theme.palette[color]?.main ?? theme.palette.primary.main, 0.04),
        flex: 1,
        minWidth: 100,
      }}
    >
      <CardContent sx={{ p: '12px 16px !important' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette[color]?.main ?? theme.palette.primary.main, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: `${color}.main`,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold" lineHeight={1.1}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function VehicleTableRow({ vehicle, idx, onEdit, onDelete, onHistory }) {
  const theme = useTheme();

  const ownerName =
    vehicle.owner_name ||
    (vehicle.owner_employee
      ? formatEmployeeThaiName(vehicle.owner_employee)
      : '-');

  const registeredBy = vehicle.employee
    ? formatEmployeeThaiName(vehicle.employee) ||
      vehicle.employee.employee_code
    : '-';

  const ownerColor = OWNER_TYPE_COLOR[vehicle.owner_type] ?? 'default';

  return (
    <TableRow
      hover
      sx={{
        '&:last-child td': { border: 0 },
        bgcolor: vehicle.is_default ? alpha(theme.palette.warning.main, 0.04) : 'inherit',
      }}
    >
      <TableCell align="center" sx={{ color: 'text.disabled', fontSize: 12, width: 40 }}>
        {idx}
      </TableCell>

      <TableCell>
        <Stack direction="row" spacing={0.6} alignItems="center">
          <CarIcon sx={{ fontSize: 15, color: 'primary.main' }} />
          <Typography variant="body2" fontWeight={700} color="primary.main" letterSpacing={0.5}>
            {vehicle.license_plate}
          </Typography>
          {vehicle.is_default && (
            <Tooltip title="รถยนต์หลัก">
              <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      <TableCell>
        <Typography variant="body2">
          {[vehicle.brand, vehicle.model_name].filter(Boolean).join(' · ') || '-'}
        </Typography>
      </TableCell>

      <TableCell>
        <Chip
          icon={OWNER_TYPE_ICON[vehicle.owner_type]}
          label={OWNER_TYPE_LABEL[vehicle.owner_type] ?? vehicle.owner_type}
          size="small"
          color={ownerColor}
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: 11 }}
        />
      </TableCell>

      <TableCell>
        <Chip
          icon={FUEL_ICON[vehicle.fuel_type]}
          label={vehicle.fuel_type ?? '-'}
          size="small"
          color={FUEL_COLOR[vehicle.fuel_type] ?? 'default'}
          sx={{ fontSize: 11 }}
        />
      </TableCell>

      <TableCell align="center">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          <SpeedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="body2">{vehicle.rate_per_km ?? 0}</Typography>
        </Stack>
      </TableCell>

      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2">{ownerName}</Typography>
          {vehicle.owner_reason && (
            <Tooltip title={vehicle.owner_reason}>
              <Typography variant="caption" color="text.disabled" sx={{ cursor: 'help' }}>(?)</Typography>
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">{registeredBy}</Typography>
      </TableCell>

      <TableCell align="center" sx={{ width: 240 }}>
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Button size="small" variant="outlined" color="info" startIcon={<HistoryIcon sx={{ fontSize: 14 }} />} onClick={() => onHistory(vehicle)} sx={{ fontSize: 12, px: 1 }}>
            ดูประวัติ
          </Button>
          <Button size="small" variant="outlined" color="primary" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => onEdit(vehicle)} sx={{ fontSize: 12, px: 1 }}>
            แก้ไข
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon sx={{ fontSize: 14 }} />} onClick={() => onDelete(vehicle)} sx={{ fontSize: 12, px: 1 }}>
            ลบ
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

const STATUS_LABEL = { PENDING: 'รออนุมัติ', APPROVED: 'อนุมัติแล้ว', REJECTED: 'ไม่อนุมัติ', PAID: 'จ่ายแล้ว' };
const STATUS_COLOR = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'error', PAID: 'info' };

// fallback parsers ใช้เมื่อ record เก่ายังไม่มี direct fields
const parseOdometerFromName = (name) => name?.match(/เลขไมล์\s*([\d,]+)\s*Km/i)?.[1] ?? null;
const parseInvoiceFromName = (name) => name?.match(/ใบกำกับ\s*(\S+)/)?.[1] ?? null;
const parseFillDateFromDesc = (desc) => desc?.match(/(\d+\/\d+\/\d+)/)?.[1] ?? null;

function FuelHistoryDialog({ open, vehicle, onClose }) {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);
  const [zoomImg, setZoomImg] = useState(null);
  const showOdo = vehicle?.owner_type === 'company';

  useEffect(() => {
    if (!open || !vehicle?.license_plate) return;
    setLoading(true);
    const q = new URLSearchParams({ license_plate: vehicle.license_plate });
    api
      .get(`${process.env.REACT_APP_API_URL}/vehicle-fuel-setting/fuel-history?${q}`)
      .then(({ data }) => setRows(Array.isArray(data) ? data : data?.result ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, vehicle?.license_plate]);

  const rowsAsc = useMemo(() => [...rows].reverse(), [rows]);

  const odoFlags = useMemo(() => {
    const flags = {};
    let prev = null;
    for (const r of rowsAsc) {
      const rawOdo = r.fuel_odometer ?? (() => {
        const s = parseOdometerFromName(r.name);
        return s ? Number(s.replace(/,/g, '')) : null;
      })();
      const val = rawOdo != null ? Number(rawOdo) : null;
      if (val !== null && prev !== null) {
        if (val < prev) flags[r._id] = 'ลดลง';
        else if (val - prev > 2000) flags[r._id] = 'กระโดดมาก';
      }
      if (val !== null) prev = val;
    }
    return flags;
  }, [rowsAsc]);

  // ระยะห่างระหว่างการเติมแต่ละครั้ง
  const odoDiff = useMemo(() => {
    const diff = {};
    let prev = null;
    for (const r of rowsAsc) {
      const rawOdo = r.fuel_odometer ?? (() => {
        const s = parseOdometerFromName(r.name);
        return s ? Number(s.replace(/,/g, '')) : null;
      })();
      const val = rawOdo != null ? Number(rawOdo) : null;
      if (val !== null && prev !== null) {
        diff[r._id] = val - prev;
      }
      if (val !== null) prev = val;
    }
    return diff;
  }, [rowsAsc]);

  const stats = useMemo(() => {
    const total = rows.reduce((s, r) => s + (r.price || 0), 0);
    const totalLiters = rows.reduce((s, r) => s + (r.fuel_liters || 0), 0);
    const avg = rows.length > 0 ? total / rows.length : 0;
    return { total, totalLiters, avg, flagCount: Object.keys(odoFlags).length };
  }, [rows, odoFlags]);

  const carLabel = [vehicle?.brand, vehicle?.model_name].filter(Boolean).join(' ');

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: 1400,
            maxHeight: '90vh',
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        {/* ── Header ── */}
        <Box sx={{
          background: `linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)`,
          px: 3, py: 2.5, color: 'white',
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{
                width: 42, height: 42, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FuelIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                  ประวัติการเติมน้ำมัน — {vehicle?.license_plate}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  {carLabel || 'ข้อมูลรถยนต์'}
                </Typography>
              </Box>
            </Stack>

            {/* summary badges */}
            {!loading && rows.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                {[
                  { v: `${rows.length} ครั้ง` },
                  { v: `฿${stats.total.toLocaleString('th-TH', { maximumFractionDigits: 0 })}` },
                  { v: `${stats.totalLiters.toLocaleString('th-TH', { maximumFractionDigits: 1 })} ล.` },
                  { v: `เฉลี่ย ฿${stats.avg.toLocaleString('th-TH', { maximumFractionDigits: 0 })}` },
                ].map((s) => (
                  <Chip key={s.v} label={s.v} size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600, fontSize: 11, border: '1px solid rgba(255,255,255,0.25)' }}
                  />
                ))}
                {stats.flagCount > 0 && (
                  <Chip label={`⚠ ${stats.flagCount} รายการผิดปกติ`} size="small"
                    sx={{ bgcolor: alpha(theme.palette.error.main, 0.85), color: 'white', fontWeight: 700, fontSize: 11 }}
                  />
                )}
              </Stack>
            )}
          </Stack>
        </Box>

        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <Box sx={{ p: 3 }}>
              {Array(5).fill(0).map((_, i) => (
                <Skeleton key={i} height={56} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 10, textAlign: 'center' }}>
              <FuelIcon sx={{ fontSize: 56, opacity: 0.15, mb: 2 }} />
              <Typography variant="h6" color="text.disabled">ยังไม่มีประวัติการเติมน้ำมัน</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table stickyHeader sx={{ '& .MuiTableCell-root': { py: 0.8, px: 2 }, whiteSpace: 'nowrap' }}>
                <TableHead>
                  <TableRow>
                    {['#', 'วันที่เติม', 'ผู้เติม', 'สถานี', ...(showOdo ? ['เลขไมล์ (km)'] : []), 'ลิตร', 'บาท/ลิตร', 'ก่อน VAT', 'VAT 7%', 'ยอดรวม', 'ใบกำกับ', 'สถานะ', 'รายละเอียด'].map((h) => (
                      <TableCell key={h} align="center" sx={{ bgcolor: 'rgb(var(--color-primary-600))' }}>
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 12 }}>{h}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => {
                    const odoVal = r.fuel_odometer ?? (() => {
                      const s = parseOdometerFromName(r.name);
                      return s ? Number(s.replace(/,/g, '')) : null;
                    })();
                    const invoice = r.invoice_number ?? parseInvoiceFromName(r.name);
                    const fillDate = r.fuel_fill_date
                      ? new Date(r.fuel_fill_date).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: '2-digit' })
                      : parseFillDateFromDesc(r.description);
                    const flag = odoFlags[r._id];
                    const empName = r.employee
                      ? formatEmployeeThaiName(r.employee) || r.employee.employee_code
                      : '-';

                    const sepDiff = idx < rows.length - 1 ? odoDiff[r._id] : null;

                    return (
                      <React.Fragment key={r._id}>
                      <TableRow
                        hover
                        sx={{
                          bgcolor: flag ? alpha(theme.palette.error.main, 0.04) : 'inherit',
                          borderLeft: flag ? `3px solid ${theme.palette.error.main}` : '3px solid transparent',
                          '&:last-child td': { border: 0 },
                        }}
                      >
                        <TableCell align="center">
                          <Typography variant="body2" color="text.disabled" fontWeight={500}>{idx + 1}</Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>{fillDate || '-'}</Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{empName}</Typography>
                        </TableCell>

                        <TableCell sx={{ maxWidth: 160 }}>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {r.fuel_station || '-'}
                          </Typography>
                        </TableCell>

                        {showOdo && (
                          <TableCell align="center">
                            {odoVal != null ? (
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                {flag && (
                                  <Tooltip title={`เลขไมล์${flag}ผิดปกติ`}>
                                    <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                  </Tooltip>
                                )}
                                <Typography variant="body2" fontWeight={flag ? 700 : 500} color={flag ? 'error.main' : 'text.primary'}>
                                  {Number(odoVal).toLocaleString('th-TH')}
                                </Typography>
                              </Stack>
                            ) : <Typography variant="body2" color="text.disabled">-</Typography>}
                          </TableCell>
                        )}

                        <TableCell align="center">
                          <Typography variant="body2">
                            {r.fuel_liters != null ? Number(r.fuel_liters).toLocaleString('th-TH', { minimumFractionDigits: 2 }) : '-'}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography variant="body2">
                            {r.fuel_price_per_liter != null ? `฿${Number(r.fuel_price_per_liter).toFixed(2)}` : '-'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {r.base_amount > 0 ? `฿${Number(r.base_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}` : '-'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {r.vat_amount > 0 ? `฿${Number(r.vat_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}` : '-'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700} color="primary.main">
                            ฿{(r.price || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', letterSpacing: 0.3 }}>
                            {invoice || '-'}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            label={STATUS_LABEL[r.status] ?? r.status}
                            size="small"
                            color={STATUS_COLOR[r.status] ?? 'default'}
                            sx={{ fontSize: 10, minWidth: 72 }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={<InfoIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setDetailTarget({ record: r, idx, fillDate, invoice, odoVal, empName, flag, diff: odoDiff[r._id] })}
                            sx={{ fontSize: 11, px: 1 }}
                          >
                            รายละเอียด
                          </Button>
                        </TableCell>
                      </TableRow>
                      {showOdo && idx < rows.length - 1 && sepDiff != null && (
                        <TableRow sx={{ height: 0 }}>
                          <TableCell colSpan={4} sx={{ p: 0, border: 0, height: 0 }} />
                          <TableCell sx={{ p: 0, border: 0, height: 0, position: 'relative', overflow: 'visible' }}>
                            <Box sx={{ position: 'absolute', top: -9, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 2 }}>
                              <Chip
                                label={`${sepDiff >= 0 ? '+' : ''}${Number(sepDiff).toLocaleString('th-TH')} km`}
                                size="small"
                                color={sepDiff < 0 ? 'error' : sepDiff > 2000 ? 'warning' : 'success'}
                                variant="outlined"
                                sx={{ fontSize: 10, fontWeight: 700, height: 18, bgcolor: 'background.paper', '& .MuiChip-label': { px: 0.8 } }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell colSpan={8} sx={{ p: 0, border: 0, height: 0 }} />
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {stats.flagCount > 0 && !loading && (
            <Box sx={{ px: 3, py: 1.5, bgcolor: alpha(theme.palette.error.main, 0.06), borderTop: `1px solid ${alpha(theme.palette.error.main, 0.15)}` }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                <Typography variant="caption" color="error.main" fontWeight={700}>
                  พบ {stats.flagCount} รายการที่เลขไมล์ผิดปกติ — ควรตรวจสอบเพิ่มเติม
                </Typography>
              </Stack>
            </Box>
          )}
        </DialogContent>

        <Box sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, minWidth: 80 }}>ปิด</Button>
        </Box>
      </Dialog>

      {/* ── Fuel Detail Dialog ── */}
      {detailTarget && (
        <FuelDetailDialog
          open={Boolean(detailTarget)}
          data={detailTarget}
          vehicle={vehicle}
          onClose={() => setDetailTarget(null)}
          onZoom={setZoomImg}
        />
      )}

      <Dialog open={Boolean(zoomImg)} onClose={() => setZoomImg(null)} maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box component="img" src={zoomImg} sx={{ width: '100%', display: 'block' }} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function FuelDetailDialog({ open, data, vehicle, onClose, onZoom }) {
  const theme = useTheme();
  if (!data) return null;
  const { record: r, idx, fillDate, invoice, odoVal, empName, flag, diff } = data;
  const fmtTHB = (v) => v != null && v > 0 ? `฿${Number(v).toLocaleString('th-TH', { minimumFractionDigits: 2 })}` : '-';

  const infoRows = [
    { label: 'วันที่เติมน้ำมัน', value: fillDate || '-', icon: '📅' },
    { label: 'ผู้ยื่นเบิก', value: empName, icon: '👤' },
    { label: 'ทะเบียนรถ (ระบบ)', value: vehicle?.license_plate || '-', icon: '🚗' },
    { label: 'ทะเบียนรถ (ในใบเสร็จ)', value: r.fuel_license_plate_in_bill || '-', icon: '🪪' },
    { label: 'สถานีบริการ', value: r.fuel_station || '-', icon: '⛽' },
    { label: 'ประเภทน้ำมัน', value: r.fuel_type_in_bill || '-', icon: '🔥' },
    { label: 'เลขไมล์', value: odoVal != null ? `${Number(odoVal).toLocaleString('th-TH')} km` : '-', icon: '🏃', warn: !!flag, warnText: flag ? `⚠ ผิดปกติ: ${flag}` : null },
    { label: 'ระยะห่างจากครั้งก่อน', value: diff != null ? `${diff >= 0 ? '+' : ''}${Number(diff).toLocaleString('th-TH')} km` : '— (ครั้งแรก)', icon: '📏', warn: diff != null && (diff < 0 || diff > 2000) },
    { label: 'จำนวนลิตร', value: r.fuel_liters != null ? `${Number(r.fuel_liters).toLocaleString('th-TH', { minimumFractionDigits: 3 })} ล.` : '-', icon: '💧' },
    { label: 'ราคาต่อลิตร', value: r.fuel_price_per_liter != null ? `฿${Number(r.fuel_price_per_liter).toFixed(2)} / ล.` : '-', icon: '💱' },
    { label: 'ราคาก่อน VAT', value: fmtTHB(r.base_amount), icon: '🧾' },
    { label: 'VAT 7%', value: fmtTHB(r.vat_amount), icon: '📊' },
    { label: 'ยอดรวมสุทธิ', value: `฿${(r.price || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`, icon: '💰', bold: true },
    { label: 'เลขที่ใบกำกับ', value: invoice || '-', icon: '📄', mono: true },
    { label: 'โปรเจค', value: r.project?.name || '-', icon: '📁' },
    { label: 'วันที่รับเงิน', value: r.date ? new Date(r.date).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-', icon: '📆' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* header */}
      <Box sx={{
        background: flag
          ? `linear-gradient(135deg, #7f1d1d 0%, ${theme.palette.error.dark} 100%)`
          : `linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)`,
        px: 3, py: 2.5, color: 'white',
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{
              width: 40, height: 40, borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FuelIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} lineHeight={1.1}>
                รายละเอียดการเติมน้ำมัน #{idx + 1}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                ทะเบียน {vehicle?.license_plate} · {fillDate || '-'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {flag && (
              <Chip
                icon={<WarningIcon sx={{ fontSize: '14px !important' }} />}
                label={`เลขไมล์${flag}`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700, fontSize: 10 }}
              />
            )}
            <Chip
              label={STATUS_LABEL[r.status] ?? r.status}
              size="small"
              color={STATUS_COLOR[r.status] ?? 'default'}
              sx={{ fontSize: 10, fontWeight: 600 }}
            />
          </Stack>
        </Stack>
      </Box>

      <DialogContent dividers sx={{ p: 2.5 }}>
        {/* info grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
          mb: 2,
        }}>
          {infoRows.map((row) => (
            <Box
              key={row.label}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: row.warn
                  ? alpha(theme.palette.error.main, 0.06)
                  : alpha(theme.palette.primary.main, 0.03),
                border: `1px solid ${row.warn ? alpha(theme.palette.error.main, 0.2) : alpha(theme.palette.divider, 0.12)}`,
              }}
            >
              <Typography variant="caption" color={row.warn ? 'error.main' : 'text.disabled'} display="block" mb={0.3}>
                {row.icon} {row.label}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={row.bold ? 800 : 600}
                color={row.warn ? 'error.main' : row.bold ? 'primary.main' : 'text.primary'}
                sx={row.mono ? { fontFamily: 'monospace', letterSpacing: 0.5, fontSize: 12 } : {}}
              >
                {row.value}
              </Typography>
              {row.warnText && (
                <Typography variant="caption" color="error.main" fontWeight={700} display="block" mt={0.3}>
                  {row.warnText}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* bill images */}
        {(r.bill_images || []).length > 0 && (
          <>
            <Divider sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>รูปภาพหลักฐาน</Typography>
            </Divider>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {r.bill_images.map((img, i) => (
                <Box key={i} onClick={() => onZoom(img.url)} sx={{ cursor: 'pointer', position: 'relative' }}>
                  <Box
                    component="img"
                    src={img.url}
                    alt={i === 0 ? 'ใบเสร็จน้ำมัน' : 'เลขไมล์'}
                    sx={{
                      width: 150,
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      display: 'block',
                      transition: 'all 0.15s',
                      '&:hover': { opacity: 0.85, transform: 'scale(1.02)' },
                    }}
                  />
                  <Chip
                    label={i === 0 ? '🧾 ใบเสร็จ' : '🏃 เลขไมล์'}
                    size="small"
                    sx={{
                      position: 'absolute', bottom: 6, left: 6,
                      bgcolor: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 10,
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* approval logs */}
        {(r.approval_logs || []).length > 0 && (
          <>
            <Divider sx={{ mt: 2, mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>ประวัติการอนุมัติ</Typography>
            </Divider>
            <Stack spacing={1}>
              {r.approval_logs.map((log, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', mt: 0.6, flexShrink: 0,
                    bgcolor: log.action === 'APPROVE' ? 'success.main' : log.action === 'REJECT' ? 'error.main' : 'text.disabled',
                  }} />
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" fontWeight={700} color="text.primary">{log.actor_name || '-'}</Typography>
                      <Chip label={log.action} size="small" sx={{ fontSize: 9, height: 16, px: 0.5 }} />
                      <Typography variant="caption" color="text.disabled">
                        {log.acted_at ? new Date(log.acted_at).toLocaleDateString('th-TH') : ''}
                      </Typography>
                    </Stack>
                    {log.note && (
                      <Typography variant="caption" color="text.secondary">{log.note}</Typography>
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>ปิด</Button>
      </DialogActions>
    </Dialog>
  );
}

function EditVehicleDialog({ open, vehicle, onClose, onSave, saving }) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (vehicle) {
      setForm({
        license_plate: vehicle.license_plate ?? '',
        brand: vehicle.brand ?? '',
        model_name: vehicle.model_name ?? '',
        fuel_type: vehicle.fuel_type ?? 'เบนซิน',
        rate_per_km: vehicle.rate_per_km ?? 4,
        owner_type: vehicle.owner_type ?? 'personal',
        owner_name: vehicle.owner_name ?? '',
        owner_reason: vehicle.owner_reason ?? '',
        is_default: vehicle.is_default ?? false,
      });
    }
  }, [vehicle]);

  const set = useCallback((field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EditIcon fontSize="small" color="primary" />
          <span>แก้ไขข้อมูลรถยนต์</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          <TextField
            label="ทะเบียนรถ *"
            value={form.license_plate}
            onChange={set('license_plate')}
            size="small"
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="ยี่ห้อ"
              value={form.brand}
              onChange={set('brand')}
              size="small"
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="รุ่น"
              value={form.model_name}
              onChange={set('model_name')}
              size="small"
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>เชื้อเพลิง</InputLabel>
              <Select
                value={form.fuel_type}
                onChange={set('fuel_type')}
                label="เชื้อเพลิง"
                sx={{ borderRadius: 2 }}
              >
                {FUEL_TYPES.map((f) => (
                  <MenuItem key={f} value={f}>{f}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="อัตรา (บ/กม.)"
              type="number"
              value={form.rate_per_km}
              onChange={set('rate_per_km')}
              size="small"
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
          <FormControl size="small" fullWidth>
            <InputLabel>ประเภทเจ้าของ</InputLabel>
            <Select
              value={form.owner_type}
              onChange={set('owner_type')}
              label="ประเภทเจ้าของ"
              sx={{ borderRadius: 2 }}
            >
              {OWNER_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{OWNER_TYPE_LABEL[t]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {form.owner_type !== 'personal' && (
            <>
              <TextField
                label="ชื่อเจ้าของ / หน่วยงาน"
                value={form.owner_name}
                onChange={set('owner_name')}
                size="small"
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="หมายเหตุ"
                value={form.owner_reason}
                onChange={set('owner_reason')}
                size="small"
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={form.is_default}
                onChange={set('is_default')}
                color="warning"
              />
            }
            label={<Typography variant="body2">ตั้งเป็นรถหลัก</Typography>}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ borderRadius: 2 }}>ยกเลิก</Button>
        <Button
          variant="contained"
          onClick={() => onSave(form)}
          disabled={saving || !form.license_plate.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ borderRadius: 2 }}
        >
          {saving ? 'กำลังบันทึก...' : 'บันทึก'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteConfirmDialog({ open, vehicle, onClose, onConfirm, deleting }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: 'error.main' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DeleteIcon fontSize="small" />
          <span>ยืนยันการลบ</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          ต้องการลบรถทะเบียน{' '}
          <Typography component="span" fontWeight={700} color="text.primary">
            {vehicle?.license_plate}
          </Typography>{' '}
          ออกจากระบบ?
        </Typography>
        <Typography variant="caption" color="error.main" mt={1} display="block">
          การลบจะไม่สามารถย้อนกลับได้
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={deleting} sx={{ borderRadius: 2 }}>ยกเลิก</Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: 2 }}
        >
          {deleting ? 'กำลังลบ...' : 'ลบ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function VehicleList({ title }) {
  const theme = useTheme();

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');

  const [historyTarget, setHistoryTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadVehicles = useCallback(() => {
    setLoading(true);
    api
      .get(`${process.env.REACT_APP_API_URL}/vehicle-fuel-setting/all-vehicles`)
      .then(({ data }) => {
        setVehicles(Array.isArray(data) ? data : data?.result ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message ?? 'โหลดข้อมูลไม่สำเร็จ');
        setLoading(false);
      });
  }, []);

  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  const stats = useMemo(
    () => ({
      total: vehicles.length,
      company: vehicles.filter((v) => v.owner_type === 'company').length,
      personal: vehicles.filter((v) => v.owner_type === 'personal').length,
      other: vehicles.filter((v) => v.owner_type === 'other').length,
    }),
    [vehicles],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return vehicles.filter((v) => {
      const matchType = filterType === 'all' || v.owner_type === filterType;
      const ownerName =
        v.owner_name ||
        (v.owner_employee
          ? `${v.owner_employee.firstname ?? ''} ${v.owner_employee.lastname ?? ''}`.trim()
          : '');
      const matchSearch =
        !q ||
        v.license_plate?.toLowerCase().includes(q) ||
        v.brand?.toLowerCase().includes(q) ||
        v.model_name?.toLowerCase().includes(q) ||
        ownerName.toLowerCase().includes(q) ||
        v.employee?.firstname?.toLowerCase().includes(q) ||
        v.employee?.lastname?.toLowerCase().includes(q);
      return matchType && matchSearch;
    });
  }, [vehicles, search, filterType]);

  const handleSave = useCallback(async (form) => {
    if (!editTarget?.setting_id || !editTarget?._id) return;
    setSaving(true);
    try {
      await api.put(
        `${process.env.REACT_APP_API_URL}/vehicle-fuel-setting/${editTarget.setting_id}/vehicle/${editTarget._id}`,
        { ...form, rate_per_km: Number(form.rate_per_km) },
      );
      setEditTarget(null);
      loadVehicles();
    } catch (err) {
      showAlert(err?.response?.data?.message ?? err.message ?? 'บันทึกไม่สำเร็จ', { status: err?.response?.status });
    } finally {
      setSaving(false);
    }
  }, [editTarget, loadVehicles]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget?.setting_id || !deleteTarget?._id) return;
    setDeleting(true);
    try {
      await api.delete(
        `${process.env.REACT_APP_API_URL}/vehicle-fuel-setting/${deleteTarget.setting_id}/vehicle/${deleteTarget._id}`,
      );
      setDeleteTarget(null);
      loadVehicles();
    } catch (err) {
      showAlert(err?.response?.data?.message ?? err.message ?? 'ลบไม่สำเร็จ', { status: err?.response?.status });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, loadVehicles]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Stack alignItems="center" spacing={1}>
          <CarIcon sx={{ fontSize: 48, color: alpha(theme.palette.primary.main, 0.3), animation: 'pulse 1.5s infinite' }} />
          <Typography color="text.secondary" variant="body2">กำลังโหลดข้อมูล...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Page title */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CarIcon color="primary" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700}>{title ?? 'รายการรถยนต์ทั้งหมด'}</Typography>
          <Typography variant="caption" color="text.secondary">ข้อมูลรถที่ลงทะเบียนในระบบตั้งเบิกน้ำมัน</Typography>
        </Box>
      </Stack>

      {/* Stats */}
      <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
        <StatCard label="รถทั้งหมด" value={stats.total} color="primary" icon={<CarIcon fontSize="small" />} />
        <StatCard label="รถบริษัท" value={stats.company} color="info" icon={<BusinessIcon fontSize="small" />} />
        <StatCard label="รถส่วนตัว" value={stats.personal} color="secondary" icon={<PersonIcon fontSize="small" />} />
        {stats.other > 0 && (
          <StatCard label="อื่น ๆ" value={stats.other} color="warning" icon={<CarIcon fontSize="small" />} />
        )}
      </Stack>

      {/* Filter bar */}
      <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, mb: 3 }}>
        <Box sx={{ p: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              placeholder="ค้นหาทะเบียน / ยี่ห้อ / เจ้าของ..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filterType}
              onChange={(_, v) => v && setFilterType(v)}
              sx={{ flexShrink: 0 }}
            >
              <ToggleButton value="all" sx={{ px: 2, fontSize: 12 }}>ทั้งหมด</ToggleButton>
              <ToggleButton value="company" sx={{ px: 2, fontSize: 12 }}>บริษัท</ToggleButton>
              <ToggleButton value="personal" sx={{ px: 2, fontSize: 12 }}>ส่วนตัว</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {filtered.length} รายการ
            </Typography>
          </Stack>
        </Box>
      </Card>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
          <TableHead>
            <TableRow className="bg-theme-600">
              <TableCell align="center"><h1 className="text-white">#</h1></TableCell>
              <TableCell align="center"><h1 className="text-white">ทะเบียน</h1></TableCell>
              <TableCell align="center"><h1 className="text-white">ยี่ห้อ / รุ่น</h1></TableCell>
              <TableCell align="center"><h1 className="text-white">ประเภท</h1></TableCell>
              <TableCell align="center"><h1 className="text-white">เชื้อเพลิง</h1></TableCell>
              <TableCell align="center"><h1 className="text-white">อัตรา (บ/กม.)</h1></TableCell>
              <TableCell align="center"><h1 className="text-white">เจ้าของ</h1></TableCell>
              <TableCell align="center"><h1 className="text-white">ลงทะเบียนโดย</h1></TableCell>
              <TableCell align="center"><h1 className="text-white">จัดการ</h1></TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length > 0 ? (
              filtered.map((v, idx) => (
                <VehicleTableRow
                  key={v._id ?? `${v.license_plate}-${idx}`}
                  vehicle={v}
                  idx={idx + 1}
                  onHistory={setHistoryTarget}
                  onEdit={setEditTarget}
                  onDelete={setDeleteTarget}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                  <Stack alignItems="center" spacing={1}>
                    <CarIcon sx={{ fontSize: 48, opacity: 0.2 }} />
                    <Typography variant="body2" color="text.secondary">ไม่พบรายการรถยนต์</Typography>
                    <Typography variant="caption" color="text.disabled">ลองเปลี่ยนคำค้นหาหรือตัวกรอง</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <FuelHistoryDialog
        open={Boolean(historyTarget)}
        vehicle={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />

      <EditVehicleDialog
        open={Boolean(editTarget)}
        vehicle={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={handleSave}
        saving={saving}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        vehicle={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        deleting={deleting}
      />
    </Container>
  );
}

VehicleList.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

StatCard.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  color: PropTypes.string,
  icon: PropTypes.node,
};

VehicleTableRow.propTypes = {
  vehicle: PropTypes.object,
  idx: PropTypes.number,
  onHistory: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

FuelHistoryDialog.propTypes = {
  open: PropTypes.bool,
  vehicle: PropTypes.object,
  onClose: PropTypes.func,
};

FuelDetailDialog.propTypes = {
  open: PropTypes.bool,
  data: PropTypes.object,
  vehicle: PropTypes.object,
  onClose: PropTypes.func,
  onZoom: PropTypes.func,
};

EditVehicleDialog.propTypes = {
  open: PropTypes.bool,
  vehicle: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
};

DeleteConfirmDialog.propTypes = {
  open: PropTypes.bool,
  vehicle: PropTypes.object,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  deleting: PropTypes.bool,
};
